import React, {
  useEffect, useLayoutEffect, useMemo, useRef, useState,
} from "react";
import styled, { keyframes, useTheme } from "styled-components";
import { tokens } from "../../activity-logs/styles";
import { IOS } from "../../activity-logs/labels";
import { formatCompact } from "../../creditos/dashboardData";
import { tokens as T } from "../../../styles/designTokens";
import { money } from "./priceData";

/**
 * Curva preço × margem, desenhada à mão em SVG para controlar cada detalhe.
 * A alça no eixo é o comando: arrastar testa outro preço, e a tela inteira acompanha.
 * Rótulos ficam nas próprias curvas, sem legenda. A faixa entre elas é a margem perdida.
 */

const TODAY = IOS.blue;
const FUTURE = IOS.green;
const LOSS = IOS.red;
const PAD = { top: 26, right: 72, bottom: 46, left: 44 };

const pct = (v) => `${v < 0 ? "−" : ""}${Math.abs(v).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
const signedPoints = (v) => `${v > 0 ? "+" : "−"}${Math.abs(v).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} p.p.`;

const draw = keyframes`from { stroke-dashoffset: 1; } to { stroke-dashoffset: 0; }`;
const fade = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const halo = keyframes`0% { r: 5; opacity: 0.45; } 70% { r: 13; opacity: 0; } 100% { r: 13; opacity: 0; }`;

const Stage = styled.div`
  position: relative;
  flex: 1;
  min-height: 390px;
  margin-top: 14px;
  user-select: none;
  touch-action: none;
  cursor: crosshair;
  font-variant-numeric: tabular-nums;

  &.dragging { cursor: ew-resize; }
  &:focus-visible { outline: none; }

  svg { position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible; }

  .curve {
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
    transition: d 260ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  .curve.enter { stroke-dasharray: 1; animation: ${draw} 720ms cubic-bezier(0.3, 0.7, 0.2, 1) both; }
  .band { transition: d 260ms cubic-bezier(0.2, 0.8, 0.2, 1); animation: ${fade} 600ms ease both; }
  .halo { animation: ${halo} 2.4s ease-out infinite; }

  @media (prefers-reduced-motion: reduce) {
    .curve, .band { transition: none; }
    .curve.enter, .band, .halo { animation: none; }
  }
`;

const Tick = styled.span`
  position: absolute;
  font-size: 10.5px;
  color: ${({ theme }) => tokens.label3(theme)};
  pointer-events: none;
  white-space: nowrap;
`;

const EndLabel = styled.span`
  position: absolute;
  transform: translateY(-50%);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: -0.01em;
  white-space: nowrap;
  pointer-events: none;
`;

const Marker = styled.span`
  position: absolute;
  font-size: 10.5px;
  line-height: 1.25;
  color: ${({ theme }) => tokens.label2(theme)};
  white-space: nowrap;
  pointer-events: none;
  b { font-weight: 600; letter-spacing: -0.015em; color: ${({ theme }) => theme.text}; }
`;

// Valor colado ao ponto em teste, sobre fundo sólido para ler em cima das curvas
const Chip = styled.span`
  position: absolute;
  transform: translateY(-50%);
  padding: 2px 7px;
  border-radius: 7px;
  font-size: 11.5px;
  font-weight: 650;
  letter-spacing: -0.02em;
  white-space: nowrap;
  pointer-events: none;
  color: ${({ $color }) => $color};
  background: ${({ theme }) => tokens.solid(theme)};
  box-shadow: 0 0 0 0.5px ${({ $color }) => `${$color}66`}, 0 4px 12px rgba(0, 0, 0, 0.28);
`;

const Gap = styled.span`
  position: absolute;
  transform: translate(calc(-100% - 12px), -50%);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: -0.015em;
  white-space: nowrap;
  color: ${LOSS};
  pointer-events: none;
`;

const Handle = styled.button`
  position: absolute;
  transform: translate(-50%, 0);
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 28px;
  padding: 0 11px 0 9px;
  border: none;
  border-radius: 999px;
  background: ${({ theme }) => (tokens.dk(theme) ? "#f5f5f7" : "#1c1c1e")};
  color: ${({ theme }) => (tokens.dk(theme) ? "#1c1c1e" : "#f5f5f7")};
  font: inherit;
  font-size: 12px;
  font-weight: 650;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  cursor: ew-resize;
  touch-action: none;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35), 0 1px 2px rgba(0, 0, 0, 0.2);
  transition: box-shadow 160ms ease, transform 160ms cubic-bezier(0.2, 0.9, 0.3, 1.2);

  .grip { display: inline-flex; gap: 2px; opacity: 0.45; }
  .grip i { width: 1.5px; height: 10px; border-radius: 1px; background: currentColor; }
  &:hover, .dragging & { transform: translate(-50%, 0) scale(1.05); }
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px ${T.brandRing}, 0 6px 18px rgba(0, 0, 0, 0.35); }
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

const Glass = styled.div`
  position: absolute;
  top: 8px;
  min-width: 152px;
  padding: 8px 11px;
  border-radius: 12px;
  font-size: 11.5px;
  color: ${({ theme }) => theme.text};
  background: ${({ theme }) => (tokens.dk(theme) ? "rgba(44, 44, 48, 0.82)" : "rgba(255, 255, 255, 0.82)")};
  backdrop-filter: blur(14px) saturate(160%);
  -webkit-backdrop-filter: blur(14px) saturate(160%);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.3), inset 0 0 0 0.5px rgba(255, 255, 255, 0.12);
  pointer-events: none;

  .p { font-weight: 650; letter-spacing: -0.02em; }
  .r { display: flex; justify-content: space-between; gap: 14px; margin-top: 3px; }
  .r span { display: inline-flex; align-items: center; gap: 5px; color: ${({ theme }) => tokens.label2(theme)}; }
  .r i { width: 6px; height: 6px; border-radius: 50%; }
  .r b { font-weight: 600; }
`;

const Hint = styled.div`
  position: absolute;
  left: ${PAD.left + 10}px;
  top: ${PAD.top - 18}px;
  font-size: 11px;
  font-weight: 300;
  color: ${({ theme }) => tokens.label2(theme)};
  pointer-events: none;
  animation: ${fade} 500ms ease both;
`;

const buildPath = (points, x, y, key) => points.map((p, i) => `${i ? "L" : "M"}${x(p.price).toFixed(1)},${y(p[key]).toFixed(1)}`).join(" ");

export default function MarginCurve({ curve, price, target, onPickPrice, segmentKey }) {
  const theme = useTheme();
  const stage = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [hover, setHover] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [touched, setTouched] = useState(false);
  const frame = useRef(0);

  useLayoutEffect(() => {
    const el = stage.current;
    if (!el) return undefined;
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => () => cancelAnimationFrame(frame.current), []);

  const {
    minPrice, maxPrice, minY, maxY, yTicks, points,
  } = curve;
  const innerW = Math.max(1, size.w - PAD.left - PAD.right);
  const innerH = Math.max(1, size.h - PAD.top - PAD.bottom);
  const x = (v) => PAD.left + ((v - minPrice) / (maxPrice - minPrice)) * innerW;
  const y = (v) => PAD.top + ((maxY - v) / (maxY - minY)) * innerH;
  const inverse = (px) => minPrice + ((px - PAD.left) / innerW) * (maxPrice - minPrice);
  const clampPrice = (v) => Math.max(minPrice, Math.min(maxPrice, v));
  const clampY = (v) => Math.max(PAD.top, Math.min(PAD.top + innerH, y(v)));

  const paths = useMemo(() => {
    if (!size.w) return null;
    const today = buildPath(points, x, y, "today");
    const future = buildPath(points, x, y, "future");
    const back = [...points].reverse().map((p) => `L${x(p.price).toFixed(1)},${y(p.future).toFixed(1)}`).join(" ");
    return { today, future, band: `${today} ${back} Z` };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, size.w, size.h, minY, maxY, minPrice, maxPrice]);

  const xTicks = useMemo(() => Array.from({ length: 5 }, (_, i) => minPrice + ((maxPrice - minPrice) * i) / 4), [minPrice, maxPrice]);

  const pxFromEvent = (e) => e.clientX - stage.current.getBoundingClientRect().left;
  const commit = (px) => {
    const value = clampPrice(inverse(px));
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => onPickPrice(Math.round(value * 100) / 100));
  };

  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    stage.current.setPointerCapture?.(e.pointerId);
    setDragging(true);
    setTouched(true);
    setHover(null);
    commit(pxFromEvent(e));
  };
  const onPointerMove = (e) => {
    const px = pxFromEvent(e);
    if (dragging) { commit(px); return; }
    if (px < PAD.left || px > PAD.left + innerW) { setHover(null); return; }
    const value = inverse(px);
    const index = Math.round(((value - minPrice) / (maxPrice - minPrice)) * (points.length - 1));
    setHover(points[Math.max(0, Math.min(points.length - 1, index))]);
  };
  const stop = () => setDragging(false);

  const onKey = (e) => {
    const stepSize = (maxPrice - minPrice) / (e.shiftKey ? 20 : 100);
    if (e.key === "ArrowRight" || e.key === "ArrowUp") { e.preventDefault(); setTouched(true); onPickPrice(Math.round(clampPrice(price + stepSize) * 100) / 100); }
    if (e.key === "ArrowLeft" || e.key === "ArrowDown") { e.preventDefault(); setTouched(true); onPickPrice(Math.round(clampPrice(price - stepSize) * 100) / 100); }
  };

  const ready = size.w > 0 && paths;
  const px = x(clampPrice(price));
  const yToday = y(curve.atToday);
  const yFuture = y(curve.atFuture);
  const gap = curve.atFuture - curve.atToday;
  const baseline = PAD.top + innerH;
  const last = points[points.length - 1];
  const showHover = hover && !dragging && Math.abs(x(hover.price) - px) > 14;
  const flipChips = px > PAD.left + innerW * 0.82;
  const zero = y(0);
  const line = tokens.separator(theme);
  const ids = `mc-${segmentKey}`;

  // Rótulos dos preços mínimos, afastados para não se tocarem
  const minLeft = Math.min(curve.minToday, curve.minFuture);
  const minRight = Math.max(curve.minToday, curve.minFuture);
  const yTarget = y(target);

  return (
    <Stage
      ref={stage}
      className={dragging ? "dragging" : ""}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={stop}
      onPointerCancel={stop}
      onPointerLeave={() => setHover(null)}
    >
      {ready && (
        <>
          <svg aria-hidden="true">
            <defs>
              <linearGradient id={`${ids}-loss`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={LOSS} stopOpacity={0.02} />
                <stop offset="100%" stopColor={LOSS} stopOpacity={0.07} />
              </linearGradient>
              <linearGradient id={`${ids}-band`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={LOSS} stopOpacity={0.02} />
                <stop offset="50%" stopColor={LOSS} stopOpacity={0.1} />
                <stop offset="100%" stopColor={LOSS} stopOpacity={0.03} />
              </linearGradient>
              <linearGradient id={`${ids}-today`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={TODAY} stopOpacity={0.35} />
                <stop offset="100%" stopColor={TODAY} />
              </linearGradient>
              <linearGradient id={`${ids}-future`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={FUTURE} stopOpacity={0.35} />
                <stop offset="100%" stopColor={FUTURE} />
              </linearGradient>
              <linearGradient id={`${ids}-rule`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={tokens.label(theme)} stopOpacity={0} />
                <stop offset="35%" stopColor={tokens.label(theme)} stopOpacity={0.5} />
                <stop offset="100%" stopColor={tokens.label(theme)} stopOpacity={0.5} />
              </linearGradient>
              <clipPath id={`${ids}-clip`}>
                <rect x={PAD.left} y={PAD.top - 6} width={innerW + 2} height={innerH + 6} />
              </clipPath>
            </defs>

            {/* Prejuízo */}
            {zero < baseline && <rect x={PAD.left} y={zero} width={innerW} height={baseline - zero} fill={`url(#${ids}-loss)`} />}

            {yTicks.map((t) => <line key={t} x1={PAD.left} x2={PAD.left + innerW} y1={y(t)} y2={y(t)} stroke={line} strokeOpacity={t === 0 ? 0 : 0.55} />)}
            <line x1={PAD.left} x2={PAD.left + innerW} y1={zero} y2={zero} stroke={tokens.label3(theme)} />
            <line x1={PAD.left} x2={PAD.left + innerW} y1={yTarget} y2={yTarget} stroke={tokens.label2(theme)} strokeDasharray="2 5" strokeLinecap="round" />

            <g clipPath={`url(#${ids}-clip)`}>
              <path className="band" d={paths.band} fill={`url(#${ids}-band)`} />
              <path key={`t-${segmentKey}`} className="curve enter" pathLength={1} d={paths.today} stroke={`url(#${ids}-today)`} style={{ filter: `drop-shadow(0 0 5px ${TODAY}55)` }} />
              <path key={`f-${segmentKey}`} className="curve enter" pathLength={1} d={paths.future} stroke={`url(#${ids}-future)`} style={{ filter: `drop-shadow(0 0 5px ${FUTURE}55)` }} />
            </g>

            {/* Equilíbrio */}
            <circle cx={x(curve.breakToday)} cy={zero} r={2.5} fill={TODAY} />
            <circle cx={x(curve.breakFuture)} cy={zero} r={2.5} fill={FUTURE} />

            {/* Preço mínimo de cada regime */}
            <circle cx={x(curve.minToday)} cy={yTarget} r={3.5} fill={tokens.solid(theme)} stroke={TODAY} strokeWidth={1.75} />
            <circle cx={x(curve.minFuture)} cy={yTarget} r={3.5} fill={tokens.solid(theme)} stroke={FUTURE} strokeWidth={1.75} />

            {/* Preço em teste */}
            <line x1={px} x2={px} y1={PAD.top - 8} y2={baseline + 8} stroke={`url(#${ids}-rule)`} />
            <line x1={px} x2={px} y1={clampY(curve.atToday)} y2={clampY(curve.atFuture)} stroke={LOSS} strokeWidth={2.5} strokeLinecap="round" opacity={gap < 0 ? 1 : 0.35} />
            <circle className="halo" cx={px} cy={clampY(curve.atFuture)} r={5} fill="none" stroke={FUTURE} strokeWidth={1.5} />
            <circle cx={px} cy={clampY(curve.atToday)} r={4.5} fill={TODAY} stroke={tokens.solid(theme)} strokeWidth={2} />
            <circle cx={px} cy={clampY(curve.atFuture)} r={4.5} fill={FUTURE} stroke={tokens.solid(theme)} strokeWidth={2} />

            {/* Ao passar o mouse */}
            {showHover && (
              <>
                <line x1={x(hover.price)} x2={x(hover.price)} y1={PAD.top} y2={baseline} stroke={tokens.label3(theme)} strokeDasharray="2 4" />
                <circle cx={x(hover.price)} cy={y(hover.today)} r={3} fill={TODAY} />
                <circle cx={x(hover.price)} cy={y(hover.future)} r={3} fill={FUTURE} />
              </>
            )}
          </svg>

          {yTicks.map((t) => <Tick key={t} style={{ left: 0, width: PAD.left - 10, textAlign: "right", top: y(t) - 7 }}>{t.toLocaleString("pt-BR")}%</Tick>)}
          {xTicks.map((t, i) => <Tick key={t} style={{ left: x(t), top: baseline + 28, transform: i === 0 ? "none" : i === 4 ? "translateX(-100%)" : "translateX(-50%)" }}>{formatCompact(t)}</Tick>)}

          <EndLabel style={{ left: x(last.price) + 8, top: y(last.today), color: TODAY }}>Hoje</EndLabel>
          <EndLabel style={{ left: x(last.price) + 8, top: y(last.future), color: FUTURE }}>CBS/IBS</EndLabel>
          <Tick style={{ left: PAD.left + innerW + 8, top: yTarget - 7, color: tokens.label2(theme) }}>Meta {pct(target)}</Tick>
          <Tick style={{ left: PAD.left + innerW + 8, top: zero - 7 }}>Equilíbrio</Tick>

          <Marker style={{ left: x(minLeft) - 7, top: yTarget - 25, transform: "translateX(-100%)", textAlign: "right" }}>
            mínimo {curve.minToday <= curve.minFuture ? "hoje" : "CBS/IBS"}<br /><b>{money(minLeft)}</b>
          </Marker>
          <Marker style={{ left: x(minRight) + 7, top: yTarget + 14 }}>
            mínimo {curve.minToday <= curve.minFuture ? "CBS/IBS" : "hoje"}<br /><b>{money(minRight)}</b>
          </Marker>

          {Math.abs(yToday - yFuture) > 22 && gap < 0 && <Gap style={{ left: px, top: (clampY(curve.atToday) + clampY(curve.atFuture)) / 2 }}>{signedPoints(gap)}</Gap>}
          <Chip $color={TODAY} style={{ top: clampY(curve.atToday), left: flipChips ? px - 12 : px + 12, transform: flipChips ? "translate(-100%, -50%)" : "translateY(-50%)" }}>{pct(curve.atToday)}</Chip>
          <Chip $color={curve.atFuture < 0 ? LOSS : FUTURE} style={{ top: clampY(curve.atFuture), left: flipChips ? px - 12 : px + 12, transform: flipChips ? "translate(-100%, -50%)" : "translateY(-50%)" }}>{pct(curve.atFuture)}</Chip>

          {showHover && (
            <Glass style={{ left: Math.min(x(hover.price) + 12, size.w - 170) }}>
              <div className="p">Cobrando {money(hover.price)}</div>
              <div className="r"><span><i style={{ background: TODAY }} />Hoje</span><b>{pct(hover.today)}</b></div>
              <div className="r"><span><i style={{ background: FUTURE }} />CBS/IBS</span><b style={{ color: hover.future < 0 ? LOSS : undefined }}>{pct(hover.future)}</b></div>
            </Glass>
          )}

          {!touched && !showHover && <Hint>Arraste a alça, ou clique em um ponto, para testar outro preço</Hint>}

          <Handle
            type="button"
            role="slider"
            aria-label="Preço em teste"
            aria-valuemin={Math.round(minPrice)}
            aria-valuemax={Math.round(maxPrice)}
            aria-valuenow={Math.round(price)}
            aria-valuetext={money(price)}
            style={{ left: Math.max(PAD.left + 44, Math.min(PAD.left + innerW - 44, px)), top: baseline - 6 }}
            onKeyDown={onKey}
          >
            <span className="grip" aria-hidden="true"><i /><i /></span>
            {money(price)}
          </Handle>
        </>
      )}
    </Stage>
  );
}
